import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useTranslation } from "react-i18next";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircle";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";

import styles from "./Support.module.css";

type SupportLink = {
  url: string;
  title: string;
};

function toLinks(value: unknown): SupportLink[] {
  const items = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? Object.values(value)
      : [];
  return items.filter(
    (link): link is SupportLink =>
      !!link &&
      typeof link === "object" &&
      typeof link.url === "string" &&
      typeof link.title === "string" &&
      link.url !== "" &&
      link.title !== "",
  );
}

export default function Support(): React.ReactNode {
  const { i18n, t } = useTranslation();

  const groupedLinks = [
    {
      title: t("support.categories.manuals.title"),
      icon: (
        <MenuBookIcon
          sx={{ fontSize: 20, color: "var(--color-green-light)" }}
        />
      ),
      links: toLinks(
        i18n.t("support.categories.manuals.links", { returnObjects: true }),
      ),
    },
    {
      title: t("support.categories.videos.title"),
      icon: (
        <PlayCircleOutlineIcon
          sx={{ fontSize: 20, color: "var(--color-green-light)" }}
        />
      ),
      links: toLinks(
        i18n.t("support.categories.videos.links", { returnObjects: true }),
      ),
    },
    {
      title: t("support.categories.guides.title"),
      icon: (
        <LightbulbOutlinedIcon
          sx={{ fontSize: 20, color: "var(--color-green-light)" }}
        />
      ),
      links: toLinks(
        i18n.t("support.categories.guides.links", { returnObjects: true }),
      ),
    },
  ];

  return (
    <div className={styles.supportPage}>
      <SupportHeader />
      <div className={styles.content}>
        <div className={styles.container}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("heading.Useful links")}
              </Typography>
              <Stack spacing={4} data-testid="support-links">
                {groupedLinks.map((group, groupIdx) => (
                  <Box key={groupIdx}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      {group.icon}
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, fontSize: "1.2rem" }}
                      >
                        {group.title}
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5} sx={{ pl: 3.5 }}>
                      {group.links.map((link, idx) => (
                        <Link
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="always"
                          sx={{
                            fontSize: "1.1rem",
                            textDecoration: "underline",
                            color: "var(--color-green-dark)",
                            display: "inline-block",
                            "&:hover": {
                              color: "var(--color-green-muted)",
                            },
                          }}
                        >
                          {link.title}
                        </Link>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: 600, mb: 3 }}
              >
                {t("heading.Get support")}
              </Typography>
              <Stack spacing={2}>
                <CustomerServiceCard />
                <WhatsAppSupportCard />
              </Stack>
            </Grid>
          </Grid>
        </div>
      </div>
      <SupportBanner />
    </div>
  );
}

function SupportHeader(): React.ReactNode {
  const { t } = useTranslation();
  return (
    <div className={styles.supportHeader}>
      <div className={styles.container}>
        <h1>{t("heading.Help and support")}</h1>
      </div>
    </div>
  );
}

const iconSx = { color: "var(--color-green-light)", fontSize: 40 };

type SupportCardProps = {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  href: string;
};

function SupportCard({
  icon,
  title,
  subtitle,
  href,
}: SupportCardProps): React.ReactNode {
  return (
    <Card
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      elevation={0}
      sx={{
        display: "block",
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          p: 3,
          "&:last-child": { pb: 3 },
        }}
      >
        {icon}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              fontSize: "1.2rem",
            }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize="1.1rem">
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function CustomerServiceCard(): React.ReactNode {
  const { t } = useTranslation();
  return (
    <SupportCard
      href="https://aureaimaging.atlassian.net/servicedesk/customer/portals"
      icon={<SupportAgentIcon sx={iconSx} />}
      title={t("heading.Customer service")}
      subtitle={t("text.Create or view a support ticket")}
    />
  );
}

function WhatsAppSupportCard(): React.ReactNode {
  const { t } = useTranslation();
  return (
    <SupportCard
      href="https://wa.me/31611194648"
      icon={<WhatsAppIcon sx={iconSx} />}
      title={t("heading.WhatsApp")}
      subtitle="+31 6 1119 4648"
    />
  );
}

function SupportBanner(): React.ReactNode {
  const { t } = useTranslation();
  return (
    <div className={styles.banner}>
      <div className={styles.container}>
        <h2>{t("heading.Need help")}</h2>
        <h3 className={styles.bannerText}>{t("text.Contact you agent")}</h3>
      </div>
    </div>
  );
}
